import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Tooltip,
  LinearProgress,
  Stack,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  History as HistoryIcon,
  SwapHoriz as SwapIcon,
  Info as InfoIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  BatteryChargingFull as BatteryIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import batteryService, {
  Battery,
  BatteryStatus,
  BatterySwapHistoryRecord,
  BatteryHealthStats,
} from '../services/batteryService';

// Battery Details Dialog Component
const BatteryDetailsDialog: React.FC<{
  open: boolean;
  battery: Battery | null;
  onClose: () => void;
  onRefresh: () => void;
}> = ({ open, battery, onClose }) => {
  const [swapHistory, setSwapHistory] = useState<BatterySwapHistoryRecord[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (battery?.id && open) {
      loadSwapHistory();
    }
  }, [battery?.id, open]);

  const loadSwapHistory = async () => {
    if (!battery?.id) return;
    setLoading(true);
    try {
      const history = await batteryService.getBatterySwapHistory(battery.id);
      setSwapHistory(history);
    } catch (error) {
      console.error('Failed to load swap history:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!battery) return null;

  const getHealthColor = (health?: number): 'success' | 'error' | 'warning' | 'inherit' => {
    if (!health) return 'inherit';
    if (health >= 80) return 'success';
    if (health >= 60) return 'warning';
    return 'error';
  };

  const getHealthChipColor = (health?: number): 'success' | 'error' | 'warning' | 'default' => {
    if (!health) return 'default';
    if (health >= 80) return 'success';
    if (health >= 60) return 'warning';
    return 'error';
  };

  const getHealthLabel = (health?: number) => {
    if (!health) return 'Unknown';
    if (health >= 80) return 'Excellent';
    if (health >= 60) return 'Good';
    if (health >= 40) return 'Fair';
    return 'Poor';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={2}>
          <BatteryIcon />
          <Box>
            <Typography variant="h6">Battery Details</Typography>
            <Typography variant="caption" color="text.secondary">
              {battery.batteryNumber}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Battery Number
                </Typography>
                <Typography variant="body1">{battery.batteryNumber}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Box>
                  <Chip
                    label={battery.status}
                    size="small"
                    color={
                      battery.status === 'ACTIVE'
                        ? 'success'
                        : battery.status === 'IN_STORAGE'
                        ? 'info'
                        : battery.status === 'UNDER_REPAIR'
                        ? 'warning'
                        : 'error'
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Manufacturer
                </Typography>
                <Typography variant="body1">
                  {battery.manufacturer || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Battery Type
                </Typography>
                <Typography variant="body1">
                  {battery.batteryType || 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Specifications */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Specifications
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Capacity
                </Typography>
                <Typography variant="body1">
                  {battery.capacity ? `${battery.capacity} Ah` : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Voltage
                </Typography>
                <Typography variant="body1">
                  {battery.voltage ? `${battery.voltage} V` : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Slot Position
                </Typography>
                <Typography variant="body1">
                  {battery.slotPosition ? `Slot ${battery.slotPosition}` : 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Health & Performance */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Health & Performance
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Battery Health
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={battery.healthPercentage || 0}
                      color={getHealthColor(battery.healthPercentage)}
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                  </Box>
                  <Typography variant="body2" fontWeight="bold">
                    {battery.healthPercentage?.toFixed(1) || 0}%
                  </Typography>
                  <Chip
                    label={getHealthLabel(battery.healthPercentage)}
                    size="small"
                    color={getHealthChipColor(battery.healthPercentage)}
                  />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Cycle Count
                </Typography>
                <Typography variant="body1">{battery.cycleCount}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Last Charge
                </Typography>
                <Typography variant="body1">
                  {battery.lastChargeDate
                    ? new Date(battery.lastChargeDate).toLocaleDateString()
                    : 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Vehicle Assignment */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Vehicle Assignment
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Parent Vehicle
                </Typography>
                <Typography variant="body1">
                  {battery.parentVehicle?.registrationNumber || 'N/A'}
                </Typography>
                {battery.parentVehicle?.model && (
                  <Typography variant="caption" color="text.secondary">
                    {battery.parentVehicle.model.name}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Current Vehicle
                </Typography>
                <Typography variant="body1">
                  {battery.currentVehicle?.registrationNumber || 'In Storage'}
                </Typography>
                {battery.currentVehicle?.model && (
                  <Typography variant="caption" color="text.secondary">
                    {battery.currentVehicle.model.name}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>

          {/* Warranty Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Warranty Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Purchase Date
                </Typography>
                <Typography variant="body1">
                  {battery.purchaseDate
                    ? new Date(battery.purchaseDate).toLocaleDateString()
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Warranty Start
                </Typography>
                <Typography variant="body1">
                  {battery.warrantyStartDate
                    ? new Date(battery.warrantyStartDate).toLocaleDateString()
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Warranty End
                </Typography>
                <Typography variant="body1">
                  {battery.warrantyEndDate
                    ? new Date(battery.warrantyEndDate).toLocaleDateString()
                    : 'N/A'}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Swap History */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Swap History ({swapHistory.length})
            </Typography>
            {loading ? (
              <CircularProgress size={24} />
            ) : swapHistory.length > 0 ? (
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>From Vehicle</TableCell>
                      <TableCell>To Vehicle</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Performed By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {swapHistory.map((swap) => (
                      <TableRow key={swap.id}>
                        <TableCell>
                          {new Date(swap.swapDate).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {swap.fromVehicle?.registrationNumber || 'Storage'}
                        </TableCell>
                        <TableCell>
                          {swap.toVehicle?.registrationNumber || 'Storage'}
                        </TableCell>
                        <TableCell>{swap.swapReason || 'N/A'}</TableCell>
                        <TableCell>{swap.performedBy || 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">No swap history available</Alert>
            )}
          </Grid>

          {/* Notes */}
          {battery.notes && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Notes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {battery.notes}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Vehicle interface for type safety
interface VehicleOption {
  id: string;
  registrationNumber: string;
  chassisNumber?: string;
  operationalStatus: string;
  batteryConfiguration?: 'SINGLE' | 'DUAL';
  batteryNumber?: string;
  battery2Number?: string;
  model?: {
    id: string;
    name: string;
    batteryConfiguration?: 'SINGLE' | 'DUAL';
  };
}

// Battery Swap Dialog Component
const BatterySwapDialog: React.FC<{
  open: boolean;
  battery: Battery | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ open, battery, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleOption | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<VehicleOption[]>([]);
  const [slotPosition, setSlotPosition] = useState<number>(1);
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedVehicle(null);
      setInputValue('');
      setOptions([]);
      setSlotPosition(1);
      setReason('');
      setRemarks('');
      setError('');
    }
  }, [open]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const searchVehicles = async (searchValue: string) => {
      setLoadingVehicles(true);
      try {
        const apiUrl = import.meta.env.VITE_API_GATEWAY_URL || 'https://testingdb.ev91riders.com';
        const response = await fetch(`${apiUrl}/api/vehicles?search=${encodeURIComponent(searchValue)}&limit=50`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.success && data.vehicles) {
          setOptions(data.vehicles);
          // If exact match found after paste, auto-select it
          if (data.vehicles.length === 1) {
            const match = data.vehicles[0];
            if (match.registrationNumber === searchValue) {
              setSelectedVehicle(match);
            }
          }
        } else if (data.success && data.data) {
          const vehiclesList = data.data.vehicles || data.data;
          setOptions(vehiclesList);
          if (vehiclesList.length === 1 && vehiclesList[0].registrationNumber === searchValue) {
            setSelectedVehicle(vehiclesList[0]);
          }
        } else {
          setOptions([]);
        }
      } catch (error: any) {
        console.error('❌ Failed to search vehicles:', error);
        setOptions([]);
      } finally {
        setLoadingVehicles(false);
      }
    };

    // Don't search if a vehicle is already selected (user clicked from dropdown)
    if (inputValue.length >= 2 && !selectedVehicle) {
      // Debounce typing (300ms)
      timeoutId = setTimeout(() => {
        searchVehicles(inputValue);
      }, 300);
    } else if (inputValue.length < 2 && !selectedVehicle) {
      setOptions([]);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [inputValue, selectedVehicle]);

  const handlePaste = (event: React.ClipboardEvent) => {
    const pastedText = event.clipboardData.getData('text').trim();
    if (pastedText.length >= 2) {
      // Immediately search on paste without debounce
      setInputValue(pastedText);
      searchVehiclesImmediately(pastedText);
    }
  };

  const searchVehiclesImmediately = async (searchValue: string) => {
    setLoadingVehicles(true);
    try {
      const apiUrl = import.meta.env.VITE_API_GATEWAY_URL || 'https://testingdb.ev91riders.com';
      const response = await fetch(`${apiUrl}/api/vehicles?search=${encodeURIComponent(searchValue)}&limit=50`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.vehicles) {
        setOptions(data.vehicles);
        // Auto-select if exact match
        if (data.vehicles.length === 1) {
          const match = data.vehicles[0];
          if (match.registrationNumber === searchValue) {
            setSelectedVehicle(match);
          }
        }
      } else if (data.success && data.data) {
        const vehiclesList = data.data.vehicles || data.data;
        setOptions(vehiclesList);
        if (vehiclesList.length === 1 && vehiclesList[0].registrationNumber === searchValue) {
          setSelectedVehicle(vehiclesList[0]);
        }
      } else {
        setOptions([]);
      }
    } catch (error: any) {
      console.error('❌ Failed to search vehicles:', error);
      setOptions([]);
    } finally {
      setLoadingVehicles(false);
    }
  };

  const handleSwap = async () => {
    if (!battery) return;

    if (!reason.trim()) {
      setError('Please provide a reason for this operation');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const performedBy = user.username || user.email || 'Unknown';

      const swapData = {
        batteryId: battery.id,
        fromVehicleId: battery.currentVehicleId || undefined,
        toVehicleId: selectedVehicle?.id || undefined,
        swapReason: reason,
        performedBy: performedBy,
        fromSlotPosition: battery.slotPosition || undefined,
        toSlotPosition: selectedVehicle ? slotPosition : undefined,
        remarks: remarks || undefined,
      };

      console.log('🔄 Initiating battery swap:', swapData);

      await batteryService.swapBattery(swapData);

      console.log('✅ Battery swap successful');
      onSuccess();
      onClose();
      // Reset form
      setSelectedVehicle(null);
      setInputValue('');
      setReason('');
      setRemarks('');
      setSlotPosition(1);
    } catch (error: any) {
      console.error('❌ Swap failed:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to swap battery';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!battery) return null;

  const isAssigning = !battery.currentVehicleId && selectedVehicle;
  const isUnassigning = battery.currentVehicleId && !selectedVehicle;

  const selectedVehicleData = selectedVehicle;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={2}>
          <SwapIcon />
          <Box>
            <Typography variant="h6">
              {isAssigning ? 'Assign' : isUnassigning ? 'Unassign' : 'Swap'} Battery
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {battery.batteryNumber}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Battery Info */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Battery Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Battery Number</Typography>
                  <Typography variant="body1"><strong>{battery.batteryNumber}</strong></Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Current Location</Typography>
                  <Typography variant="body1">
                    {battery.currentVehicle?.registrationNumber || 'Storage'}
                    {battery.slotPosition && ` (Slot ${battery.slotPosition})`}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Swap Type</Typography>
                  <Typography variant="body1" color="primary">
                    <strong>
                      {isAssigning ? 'Storage to Vehicle' : isUnassigning ? 'Vehicle to Storage' : 'Vehicle to Vehicle'}
                    </strong>
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Vehicle Selection */}
          <FormControl fullWidth>
            <Autocomplete
              fullWidth
              freeSolo
              clearOnBlur={false}
              blurOnSelect={false}
              options={options}
              value={selectedVehicle}
              onChange={(_, newValue) => {
                if (typeof newValue !== 'string') {
                  setSelectedVehicle(newValue);
                }
              }}
              onInputChange={(_, newInputValue) => {
                setInputValue(newInputValue);
              }}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : `${option.registrationNumber} - ${option.model?.name || 'Unknown'}`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={loadingVehicles}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Target Vehicle (Leave empty for storage)"
                  placeholder="Search by registration number..."
                  onPaste={handlePaste}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingVehicles ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  helperText="Type 2+ characters or paste registration number. Leave empty to move to storage."
                />
              )}
            />
          </FormControl>

          {selectedVehicle && (
            <FormControl fullWidth>
              <InputLabel>Slot Position</InputLabel>
              <Select
                value={slotPosition}
                onChange={(e) => setSlotPosition(Number(e.target.value))}
                label="Slot Position"
              >
                <MenuItem value={1}>
                  Slot 1
                  {selectedVehicleData?.batteryNumber && (
                    <Typography component="span" variant="caption" color="error" sx={{ ml: 1 }}>
                      (Occupied)
                    </Typography>
                  )}
                </MenuItem>
                <MenuItem
                  value={2}
                  disabled={
                    selectedVehicleData?.batteryConfiguration !== 'DUAL' &&
                    selectedVehicleData?.model?.batteryConfiguration !== 'DUAL'
                  }
                >
                  Slot 2
                  {selectedVehicleData?.battery2Number && (
                    <Typography component="span" variant="caption" color="error" sx={{ ml: 1 }}>
                      (Occupied)
                    </Typography>
                  )}
                  {selectedVehicleData &&
                   selectedVehicleData.batteryConfiguration !== 'DUAL' &&
                   selectedVehicleData.model?.batteryConfiguration !== 'DUAL' && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      (Single config only)
                    </Typography>
                  )}
                </MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Swap Reason */}
          <TextField
            fullWidth
            required
            label="Swap Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Scheduled maintenance, Battery replacement, etc."
            error={!!error && !reason.trim()}
          />

          {/* Remarks */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Remarks (Optional)"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Additional notes about this swap..."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSwap}
          variant="contained"
          color="primary"
          disabled={loading || !reason.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : <SwapIcon />}
        >
          {loading ? 'Swapping...' : 'Swap Battery'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


// Add/Create Battery Dialog Component
const CreateBatteryDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [error, setError] = useState('');
  const [parentInputValue, setParentInputValue] = useState('');
  const [currentInputValue, setCurrentInputValue] = useState('');
  const [parentOptions, setParentOptions] = useState<any[]>([]);
  const [currentOptions, setCurrentOptions] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    batteryNumber: '',
    manufacturer: '',
    batteryType: '',
    capacity: '',
    voltage: '',
    healthPercentage: '100',
    cycleCount: '0',
    lastChargeDate: '',
    warrantyStartDate: '',
    warrantyEndDate: '',
    purchaseDate: '',
    status: BatteryStatus.IN_STORAGE,
    slotPosition: '',
    notes: '',
    parentVehicleId: '',
    currentVehicleId: '',
  });

  useEffect(() => {
    if (open) {
      setFormData({
        batteryNumber: '',
        manufacturer: '',
        batteryType: '',
        capacity: '',
        voltage: '',
        healthPercentage: '100',
        cycleCount: '0',
        lastChargeDate: '',
        warrantyStartDate: '',
        warrantyEndDate: '',
        purchaseDate: '',
        status: BatteryStatus.IN_STORAGE,
        slotPosition: '',
        notes: '',
        parentVehicleId: '',
        currentVehicleId: '',
      });
      setError('');
      setParentInputValue('');
      setCurrentInputValue('');
      setParentOptions([]);
      setCurrentOptions([]);
    }
  }, [open]);

  // search parent vehicles
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const searchVehicles = async (searchValue: string) => {
      setLoadingVehicles(true);
      try {
        const apiUrl = import.meta.env.VITE_API_GATEWAY_URL || 'https://testingdb.ev91riders.com';
        const response = await fetch(`${apiUrl}/api/vehicles?search=${encodeURIComponent(searchValue)}&limit=50`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data.success && data.vehicles) {
          setParentOptions(data.vehicles);
        } else if (data.success && data.data) {
          const vehiclesList = data.data.vehicles || data.data;
          setParentOptions(vehiclesList);
        } else {
          setParentOptions([]);
        }
      } catch (err: any) {
        console.error('Failed to search vehicles:', err);
        setParentOptions([]);
      } finally {
        setLoadingVehicles(false);
      }
    };

    if (parentInputValue.length >= 2 && !formData.parentVehicleId) {
      timeoutId = setTimeout(() => {
        searchVehicles(parentInputValue);
      }, 300);
    } else if (parentInputValue.length < 2 && !formData.parentVehicleId) {
      setParentOptions([]);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [parentInputValue, formData.parentVehicleId]);

  // search current vehicles
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const searchVehicles = async (searchValue: string) => {
      setLoadingVehicles(true);
      try {
        const apiUrl = import.meta.env.VITE_API_GATEWAY_URL || 'https://testingdb.ev91riders.com';
        const response = await fetch(`${apiUrl}/api/vehicles?search=${encodeURIComponent(searchValue)}&limit=50`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data.success && data.vehicles) {
          setCurrentOptions(data.vehicles);
        } else if (data.success && data.data) {
          const vehiclesList = data.data.vehicles || data.data;
          setCurrentOptions(vehiclesList);
        } else {
          setCurrentOptions([]);
        }
      } catch (err: any) {
        console.error('Failed to search vehicles:', err);
        setCurrentOptions([]);
      } finally {
        setLoadingVehicles(false);
      }
    };

    if (currentInputValue.length >= 2 && !formData.currentVehicleId) {
      timeoutId = setTimeout(() => {
        searchVehicles(currentInputValue);
      }, 300);
    } else if (currentInputValue.length < 2 && !formData.currentVehicleId) {
      setCurrentOptions([]);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [currentInputValue, formData.currentVehicleId]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.batteryNumber.trim()) {
      setError('Battery number is required');
      return;
    }
    if (!formData.parentVehicleId) {
      setError('Parent vehicle is required');
      return;
    }
    if (formData.capacity && parseFloat(formData.capacity) <= 0) {
      setError('Capacity must be positive');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload: any = {
        batteryNumber: formData.batteryNumber.trim(),
        parentVehicleId: formData.parentVehicleId,
        status: formData.status,
      };
      if (formData.manufacturer) payload.manufacturer = formData.manufacturer.trim();
      if (formData.batteryType) payload.batteryType = formData.batteryType.trim();
      if (formData.capacity) payload.capacity = parseFloat(formData.capacity);
      if (formData.voltage) payload.voltage = parseFloat(formData.voltage);
      if (formData.healthPercentage) payload.healthPercentage = parseFloat(formData.healthPercentage);
      if (formData.cycleCount) payload.cycleCount = parseInt(formData.cycleCount);
      if (formData.lastChargeDate) payload.lastChargeDate = formData.lastChargeDate;
      if (formData.warrantyStartDate) payload.warrantyStartDate = formData.warrantyStartDate;
      if (formData.warrantyEndDate) payload.warrantyEndDate = formData.warrantyEndDate;
      if (formData.purchaseDate) payload.purchaseDate = formData.purchaseDate;
      if (formData.slotPosition) payload.slotPosition = parseInt(formData.slotPosition);
      if (formData.notes) payload.notes = formData.notes.trim();
      if (formData.currentVehicleId) payload.currentVehicleId = formData.currentVehicleId;

      await batteryService.createBattery(payload);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create battery');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add New Battery</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Basic Information */}
          <Typography variant="subtitle2" color="primary">Basic Information</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Battery Number"
                value={formData.batteryNumber}
                onChange={(e) => handleChange('batteryNumber', e.target.value)}
                placeholder="e.g., BATT-001"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Manufacturer"
                value={formData.manufacturer}
                onChange={(e) => handleChange('manufacturer', e.target.value)}
                placeholder="e.g., Panasonic"
              />
            </Grid>
          </Grid>

          {/* Specifications */}
          <Typography variant="subtitle2" color="primary">Specifications</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Battery Type"
                value={formData.batteryType}
                onChange={(e) => handleChange('batteryType', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Capacity (kWh)"
                value={formData.capacity}
                onChange={(e) => handleChange('capacity', e.target.value)}
                InputProps={{ inputProps: { min: 0, step: 0.1 } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Voltage (V)"
                value={formData.voltage}
                onChange={(e) => handleChange('voltage', e.target.value)}
                InputProps={{ inputProps: { min: 0, step: 0.1 } }}
              />
            </Grid>
          </Grid>

          {/* Vehicle Assignment */}
          <Typography variant="subtitle2" color="primary">Vehicle Assignment</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                freeSolo
                clearOnBlur={false}
                blurOnSelect={false}
                options={parentOptions}
                value={parentOptions.find((v: any) => v.id === formData.parentVehicleId) || null}
                onChange={(_, newValue) => {
                  if (typeof newValue !== 'string') {
                    handleChange('parentVehicleId', newValue?.id || '');
                  }
                }}
                onInputChange={(_, newInputValue) => {
                  setParentInputValue(newInputValue);
                }}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : `${option.registrationNumber} - ${option.model?.name || 'Unknown'}`
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={loadingVehicles}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Parent Vehicle *"
                    required
                    placeholder="Search by registration number..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingVehicles ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    helperText="Type 2+ characters to search for parent vehicle"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                freeSolo
                clearOnBlur={false}
                blurOnSelect={false}
                options={currentOptions}
                value={formData.currentVehicleId ? currentOptions.find((v: any) => v.id === formData.currentVehicleId) || null : null}
                onChange={(_, newValue) => {
                  if (typeof newValue !== 'string') {
                    handleChange('currentVehicleId', newValue?.id || '');
                  }
                }}
                onInputChange={(_, newInputValue) => {
                  setCurrentInputValue(newInputValue);
                }}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : `${option.registrationNumber} - ${option.model?.name || 'Unknown'}`
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={loadingVehicles}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Current Vehicle (Optional)"
                    placeholder="Search by registration number..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingVehicles ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    helperText="Type 2+ characters or leave empty for storage"
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Health & Usage */}
          <Typography variant="subtitle2" color="primary">Health & Usage</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Health Percentage"
                value={formData.healthPercentage}
                onChange={(e) => handleChange('healthPercentage', e.target.value)}
                InputProps={{ inputProps: { min: 0, max: 100 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Cycle Count"
                value={formData.cycleCount}
                onChange={(e) => handleChange('cycleCount', e.target.value)}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
          </Grid>

          {/* Warranty & Purchase */}
          <Typography variant="subtitle2" color="primary">Warranty & Purchase</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Purchase Date"
                value={formData.purchaseDate}
                onChange={(e) => handleChange('purchaseDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Warranty Start Date"
                value={formData.warrantyStartDate}
                onChange={(e) => handleChange('warrantyStartDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Warranty End Date"
                value={formData.warrantyEndDate}
                onChange={(e) => handleChange('warrantyEndDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {/* Status & Slot */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value={BatteryStatus.ACTIVE}>Active</MenuItem>
                  <MenuItem value={BatteryStatus.IN_STORAGE}>In Storage</MenuItem>
                  <MenuItem value={BatteryStatus.UNDER_REPAIR}>Under Repair</MenuItem>
                  <MenuItem value={BatteryStatus.DAMAGED}>Damaged</MenuItem>
                  <MenuItem value={BatteryStatus.RETIRED}>Retired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Slot Position"
                value={formData.slotPosition}
                onChange={(e) => handleChange('slotPosition', e.target.value)}
                InputProps={{ inputProps: { min: 1 } }}
              />
            </Grid>
          </Grid>

          {/* Notes */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (Optional)"
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Additional comments..."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Create Battery'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Status Update Dialog Component
const StatusUpdateDialog: React.FC<{
  open: boolean;
  battery: Battery | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ open, battery, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<BatteryStatus | ''>('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && battery) {
      setSelectedStatus(battery.status);
      setError('');
    }
  }, [open, battery]);

  const handleUpdateStatus = async () => {
    if (!battery || !selectedStatus) return;

    setLoading(true);
    setError('');

    try {
      await batteryService.updateBatteryStatus(battery.batteryNumber, selectedStatus);
      onSuccess();
      onClose();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update battery status');
    } finally {
      setLoading(false);
    }
  };

  if (!battery) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Battery Status</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Battery:</strong> {battery.batteryNumber}
            <br />
            <strong>Current Status:</strong> {battery.status.replace('_', ' ')}
          </Alert>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <FormControl fullWidth>
            <InputLabel>New Status</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as BatteryStatus)}
              label="New Status"
              disabled={loading}
            >
              <MenuItem value={BatteryStatus.UNDER_REPAIR}>Under Repair</MenuItem>
              <MenuItem value={BatteryStatus.DAMAGED}>Damaged</MenuItem>
              <MenuItem value={BatteryStatus.RETIRED}>Retired</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpdateStatus}
          variant="contained"
          color="primary"
          disabled={loading || !selectedStatus || selectedStatus === battery.status}
        >
          {loading ? 'Updating...' : 'Update Status'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Battery Inventory Component
const BatteryInventory: React.FC = () => {
  const [batteries, setBatteries] = useState<Battery[]>([]);
  const [filteredBatteries, setFilteredBatteries] = useState<Battery[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedBattery, setSelectedBattery] = useState<Battery | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [swapDialogOpen, setSwapDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [statusUpdateDialogOpen, setStatusUpdateDialogOpen] = useState(false);
  const [stats, setStats] = useState<BatteryHealthStats | null>(null);

  useEffect(() => {
    loadBatteries();
    loadStats();
  }, []);

  useEffect(() => {
    filterBatteries();
  }, [batteries, searchTerm, statusFilter]);

  const loadBatteries = async () => {
    setLoading(true);
    try {
      const data = await batteryService.getAllBatteries();
      setBatteries(data);
    } catch (error) {
      console.error('Failed to load batteries:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await batteryService.getBatteryHealthStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleCreateSuccess = () => {
    loadBatteries();
    loadStats();
  };

  const filterBatteries = () => {
    let filtered = batteries;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (battery) =>
          battery.batteryNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
          battery.manufacturer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          battery.parentVehicle?.registrationNumber
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          battery.currentVehicle?.registrationNumber
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter((battery) => battery.status === statusFilter);
    }

    setFilteredBatteries(filtered);
  };

  const handleDetailsClick = (battery: Battery) => {
    setSelectedBattery(battery);
    setDetailsDialogOpen(true);
  };

  const handleSwapClick = (battery: Battery) => {
    setSelectedBattery(battery);
    setSwapDialogOpen(true);
  };

  const handleSwapSuccess = () => {
    loadBatteries();
    loadStats();
  };

  const handleStatusUpdateClick = (battery: Battery) => {
    setSelectedBattery(battery);
    setStatusUpdateDialogOpen(true);
  };

  const handleStatusUpdateSuccess = () => {
    loadBatteries();
    loadStats();
  };

  const getHealthIcon = (health?: number) => {
    if (!health) return <WarningIcon color="disabled" />;
    if (health >= 80) return <CheckCircleIcon color="success" />;
    if (health >= 60) return <WarningIcon color="warning" />;
    return <ErrorIcon color="error" />;
  };

  const getStatusColor = (status: BatteryStatus) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'IN_STORAGE':
        return 'info';
      case 'UNDER_REPAIR':
        return 'warning';
      case 'DAMAGED':
      case 'RETIRED':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, overflowX: 'hidden' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.55rem', md: '2.125rem' }, lineHeight: 1.15 }}>
          Battery Inventory Management
        </Typography>
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1, '& .MuiButton-root': { flex: { xs: '1 1 calc(50% - 8px)', sm: '0 0 auto' }, minWidth: { xs: 0, sm: 120 } } }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => {
              // Export functionality
              console.log('Export batteries');
            }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Add Battery
          </Button>
        </Stack>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={{ xs: 1.25, md: 2 }} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Total Batteries
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {stats.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Active
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {stats.active}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  In Storage
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="info.main">
                  {stats.inStorage}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Under Repair
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  {stats.underRepair}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Damaged
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="error.main">
                  {stats.damaged}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Retired
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="text.secondary">
                  {stats.retired}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
        <Grid container spacing={{ xs: 1.25, md: 2 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by battery number, manufacturer, or vehicle..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status Filter"
              >
                <MenuItem value="ALL">All Statuses</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="IN_STORAGE">In Storage</MenuItem>
                <MenuItem value="UNDER_REPAIR">Under Repair</MenuItem>
                <MenuItem value="DAMAGED">Damaged</MenuItem>
                <MenuItem value="RETIRED">Retired</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredBatteries.length} of {batteries.length} batteries
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Battery Table */}
      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 920 }}>
            <TableHead>
              <TableRow>
                <TableCell>Battery Number</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Health</TableCell>
                <TableCell>Current Vehicle</TableCell>
                <TableCell>Parent Vehicle</TableCell>
                <TableCell>Manufacturer</TableCell>
                <TableCell>Cycle Count</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredBatteries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary">No batteries found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredBatteries
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((battery) => (
                    <TableRow key={battery.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {battery.batteryNumber}
                        </Typography>
                        {battery.slotPosition && (
                          <Typography variant="caption" color="text.secondary">
                            Slot {battery.slotPosition}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={battery.status.replace('_', ' ')}
                          size="small"
                          color={getStatusColor(battery.status)}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {getHealthIcon(battery.healthPercentage)}
                          <Typography variant="body2">
                            {battery.healthPercentage?.toFixed(0) || 0}%
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {battery.currentVehicle?.registrationNumber || (
                            <em style={{ color: '#999' }}>In Storage</em>
                          )}
                        </Typography>
                        {battery.currentVehicle?.model && (
                          <Typography variant="caption" color="text.secondary">
                            {battery.currentVehicle.model.name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {battery.parentVehicle?.registrationNumber}
                        </Typography>
                        {battery.parentVehicle?.model && (
                          <Typography variant="caption" color="text.secondary">
                            {battery.parentVehicle.model.name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {battery.manufacturer || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{battery.cycleCount}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleDetailsClick(battery)}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Update Status">
                            <IconButton
                              size="small"
                              onClick={() => handleStatusUpdateClick(battery)}
                              color="secondary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Assign/Swap Battery">
                            <IconButton
                              size="small"
                              onClick={() => handleSwapClick(battery)}
                              color="primary"
                            >
                              <SwapIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View History">
                            <IconButton
                              size="small"
                              onClick={() => handleDetailsClick(battery)}
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredBatteries.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Dialogs */}
      <BatteryDetailsDialog
        open={detailsDialogOpen}
        battery={selectedBattery}
        onClose={() => setDetailsDialogOpen(false)}
        onRefresh={loadBatteries}
      />

      <BatterySwapDialog
        open={swapDialogOpen}
        battery={selectedBattery}
        onClose={() => setSwapDialogOpen(false)}
        onSuccess={handleSwapSuccess}
      />

      <CreateBatteryDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      <StatusUpdateDialog
        open={statusUpdateDialogOpen}
        battery={selectedBattery}
        onClose={() => setStatusUpdateDialogOpen(false)}
        onSuccess={handleStatusUpdateSuccess}
      />
    </Box>
  );
};

export default BatteryInventory;
